import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Wrench, Gem, Handshake, Shield } from 'lucide-react';

const WhyUsSection = () => {
  const { t } = useTranslation();

  const features = [
    { 
      icon: Wrench, 
      title: t('whyUs.features.service.title'), 
      desc: t('whyUs.features.service.description') 
    },
    { 
      icon: Gem, 
      title: t('whyUs.features.value.title'), 
      desc: t('whyUs.features.value.description') 
    },
    { 
      icon: Handshake, 
      title: t('whyUs.features.partners.title'), 
      desc: t('whyUs.features.partners.description') 
    },
    { 
      icon: Shield, 
      title: t('whyUs.features.integrity.title'), 
      desc: t('whyUs.features.integrity.description') 
    },
  ];

  return (
    <section data-testid="why-us-section" className="section-padding bg-secondary grain">
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-text mb-4 block">{t('whyUs.label')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-12">
              {t('whyUs.title')}
            </h2>

            <div className="grid sm:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`why-us-feature-${index}`}
                  className="group"
                >
                  <div className="w-14 h-14 bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                    <feature.icon className="text-gold" size={26} />
                  </div>
                  <h4 className="font-semibold text-foreground text-lg mb-2">{feature.title}</h4>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1768270180943-782e0a905e92"
                alt="Modern kitchen"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Stats Overlay */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute -bottom-8 start-8 end-8 bg-brand-panel text-white p-8 grid grid-cols-2 gap-6"
            >
              <div className="text-center">
                <span className="text-gold text-4xl font-bold">60+</span>
                <span className="block text-white/60 text-sm mt-1">{t('stats.completedProjects')}</span>
              </div>
              <div className="text-center">
                <span className="text-gold text-4xl font-bold">100%</span>
                <span className="block text-white/60 text-sm mt-1">{t('whyUs.features.value.title')}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
